import ProjRef from "@/shared/components/ProjRef";
import { useEffect, useState, useCallback } from "react";
import api, { API, fmtErr } from "@/shared/lib/api";
import FileUpload from "@/shared/components/FileUpload";
import { PageHeader } from "@/shared/components/UI";
import { Truck, ArrowLeft, CheckCircle2, FileText, Eye, Package, ChevronRight } from "lucide-react";
import { toast } from "sonner";

const BASE = API.replace(/\/api$/, "");
const fullUrl = (u) => (!u ? "" : u.startsWith("http") ? u : `${BASE}${u}`);

export default function DCUploadPortal() {
  const [data, setData] = useState(null);
  const [code, setCode] = useState("");
  const load = useCallback(() => {
    api.get("/dc-portal").then((r) => setData(r.data)).catch(() => setData({ projects: [] }));
  }, []);
  useEffect(() => { load(); }, [load]);
  if (!data) return <div className="p-8 text-sm text-zinc-400">Loading delivery challans…</div>;
  const active = (data.projects || []).find((p) => p.project_code === code);
  return (
    <div className="p-6 sm:p-8 max-w-6xl mx-auto">
      <PageHeader kicker="Logistics" title="Delivery Challans"
                  subtitle="Mark delivered items and attach the DC — one upload per challan." />
      {!active ? (
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-6">
          {(data.projects || []).length === 0 && (
            <div className="text-sm text-zinc-400 col-span-full">No projects assigned to you yet.</div>
          )}
          {(data.projects || []).map((p) => (
            <button key={p.project_code} onClick={() => setCode(p.project_code)}
                    data-testid={`dc-project-${p.project_code}`}
                    className="text-left bg-white border border-zinc-200 rounded-xl p-4 hover:border-orange-300 hover:shadow-sm transition-all">
              <div className="font-mono text-[11px] text-orange-600 font-semibold"><ProjRef code={p.project_code} /></div>
              <div className="font-medium text-zinc-900 mt-0.5 truncate">{p.project_name}</div>
              <div className="text-xs text-zinc-500 mt-2 flex items-center gap-1.5">
                <Package className="w-3.5 h-3.5" strokeWidth={1.5} />
                {p.delivered_count}/{p.item_count} items delivered · {p.dcs.length} DC{p.dcs.length !== 1 ? "s" : ""}
              </div>
            </button>
          ))}
        </div>
      ) : (
        <ProjectDC p={active} onBack={() => setCode("")} onDone={load} />
      )}
    </div>
  );
}

function ProjectDC({ p, onBack, onDone }) {
  const [sel, setSel] = useState(new Set());
  const [open, setOpen] = useState(new Set());
  const toggleOpen = (scope) => setOpen((s) => { const n = new Set(s); n.has(scope) ? n.delete(scope) : n.add(scope); return n; });
  const [dcNo, setDcNo] = useState("");
  const [fileUrl, setFileUrl] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const pending = p.items.filter((i) => !i.delivered);
  const toggle = (id) => setSel((s) => { const n = new Set(s); n.has(id) ? n.delete(id) : n.add(id); return n; });
  const toggleScope = (items) => {
    const ids = items.filter((i) => !i.delivered).map((i) => i.boq_id);
    setSel((s) => {
      const n = new Set(s);
      const allOn = ids.length > 0 && ids.every((id) => n.has(id));
      ids.forEach((id) => (allOn ? n.delete(id) : n.add(id)));
      return n;
    });
  };
  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/projects/${p.project_code}/dc-uploads`, {
        dc_number: dcNo, file_url: fileUrl, item_ids: [...sel], note,
      });
      toast.success(`DC ${dcNo} uploaded — ${sel.size} item(s) marked delivered`);
      setSel(new Set()); setDcNo(""); setFileUrl(""); setNote("");
      onDone();
    } catch (e) { toast.error(fmtErr(e.response?.data?.detail) || "Failed"); }
    finally { setBusy(false); }
  };
  const groups = {};
  p.items.forEach((i) => { (groups[i.scope] = groups[i.scope] || []).push(i); });
  return (
    <div className="mt-4">
      <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-zinc-500 hover:text-zinc-900 mb-4" data-testid="dc-back">
        <ArrowLeft className="w-4 h-4" /> All projects
      </button>
      <div className="grid lg:grid-cols-[1fr,360px] gap-6 items-start">
        <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
          <div className="px-4 py-3 border-b border-zinc-100 bg-zinc-50/60 flex items-center justify-between">
            <h3 className="font-display text-sm font-bold"><ProjRef code={p.project_code} /> · Items ({p.delivered_count}/{p.item_count} delivered)</h3>
            {pending.length > 0 && (
              <button onClick={() => setSel(new Set(sel.size === pending.length ? [] : pending.map((i) => i.boq_id)))}
                      className="text-[11px] font-semibold text-orange-600 hover:underline" data-testid="dc-select-all">
                {sel.size === pending.length ? "Clear all" : "Select all pending"}
              </button>
            )}
          </div>
          <div className="divide-y divide-zinc-50 max-h-[560px] overflow-y-auto">
            {Object.entries(groups).map(([scope, items]) => {
              const pendingIds = items.filter((i) => !i.delivered).map((i) => i.boq_id);
              const allPicked = pendingIds.length > 0 && pendingIds.every((id) => sel.has(id));
              const somePicked = pendingIds.some((id) => sel.has(id));
              const deliveredN = items.filter((i) => i.delivered).length;
              const isOpen = open.has(scope);
              return (
              <div key={scope}>
                <div className="px-3 py-2.5 bg-zinc-50/80 flex items-center gap-2.5 cursor-pointer hover:bg-zinc-100/80 select-none"
                     onClick={() => toggleOpen(scope)} data-testid={`dc-scope-row-${scope}`}>
                  <ChevronRight className={`w-4 h-4 text-zinc-400 flex-shrink-0 transition-transform ${isOpen ? "rotate-90" : ""}`} strokeWidth={2} />
                  {pendingIds.length > 0 ? (
                    <input type="checkbox" checked={allPicked}
                           ref={(el) => el && (el.indeterminate = somePicked && !allPicked)}
                           onChange={() => toggleScope(items)}
                           onClick={(e) => e.stopPropagation()}
                           className="w-4 h-4 accent-orange-600 flex-shrink-0"
                           data-testid={`dc-scope-pick-${scope}`} />
                  ) : (
                    <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0" strokeWidth={1.8} />
                  )}
                  <span className="text-xs font-mono uppercase tracking-widest text-zinc-700 font-semibold flex-1 truncate">{scope}</span>
                  {somePicked && (
                    <span className="px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-700 text-[10px] font-bold">
                      {pendingIds.filter((id) => sel.has(id)).length} selected
                    </span>
                  )}
                  <span className={`text-[10px] font-semibold ${deliveredN === items.length ? "text-emerald-600" : "text-zinc-400"}`}>{deliveredN}/{items.length} delivered</span>
                </div>
                {isOpen && items.map((it) => (
                  <div key={it.boq_id} className={`pl-9 pr-4 py-2 flex items-center gap-3 ${it.delivered ? "bg-emerald-50/30" : "hover:bg-zinc-50"}`}
                       data-testid={`dc-item-${it.boq_id}`}>
                    {it.delivered ? (
                      <CheckCircle2 className="w-4 h-4 text-emerald-500 flex-shrink-0" strokeWidth={1.8} />
                    ) : (
                      <input type="checkbox" checked={sel.has(it.boq_id)} onChange={() => toggle(it.boq_id)}
                             className="w-4 h-4 accent-orange-600 flex-shrink-0" data-testid={`dc-pick-${it.boq_id}`} />
                    )}
                    <span className={`w-[42%] text-sm truncate ${it.delivered ? "text-zinc-500" : "text-zinc-800"}`}>{it.label}</span>
                    <span className="flex-1 text-xs text-zinc-400 truncate">{it.group || "—"}</span>
                    {it.delivered && (
                      <span className="flex items-center gap-2">
                        <span className="px-1.5 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold uppercase tracking-wider">Delivered</span>
                        {it.dc_file_url ? (
                          <a href={fullUrl(it.dc_file_url)} target="_blank" rel="noreferrer"
                             className="font-mono text-[11px] font-semibold text-blue-600 hover:underline"
                             title="View delivery challan" data-testid={`dc-view-${it.boq_id}`}>
                            {it.dc_number}
                          </a>
                        ) : (
                          <span className="font-mono text-[11px] text-zinc-500">{it.dc_number}</span>
                        )}
                      </span>
                    )}
                  </div>
                ))}
              </div>
            );})}
          </div>
        </div>
        <div className="space-y-4">
          <div className="bg-white border border-zinc-200 rounded-xl p-4">
            <div className="flex items-center gap-2 mb-3">
              <Truck className="w-4 h-4 text-orange-500" strokeWidth={1.5} />
              <h3 className="font-display text-sm font-bold">Upload delivery challan</h3>
            </div>
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1">DC number</label>
            <input value={dcNo} onChange={(e) => setDcNo(e.target.value)} placeholder="e.g. DC-2026-0144"
                   className="w-full h-9 px-3 rounded-md border border-zinc-300 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-orange-300"
                   data-testid="dc-number-input" />
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1">DC file (PDF / photo)</label>
            <FileUpload value={fileUrl} onChange={setFileUrl} label="Attach signed DC"
                        accept=".pdf,.png,.jpg,.jpeg,.webp" testid="dc-file-upload" />
            <label className="block text-[11px] font-semibold uppercase tracking-wider text-zinc-500 mb-1 mt-3">Note (optional)</label>
            <input value={note} onChange={(e) => setNote(e.target.value)}
                   className="w-full h-9 px-3 rounded-md border border-zinc-300 text-sm mb-3 focus:outline-none focus:ring-2 focus:ring-orange-300"
                   data-testid="dc-note-input" />
            <button onClick={submit} disabled={busy || !dcNo.trim() || !fileUrl || sel.size === 0}
                    className="w-full h-10 rounded-lg bg-zinc-900 text-white text-sm font-semibold hover:bg-zinc-800 disabled:opacity-40 transition-colors"
                    data-testid="dc-submit">
              {busy ? "Uploading…" : `Mark ${sel.size || ""} item${sel.size === 1 ? "" : "s"} delivered`}
            </button>
          </div>
          {p.dcs.length > 0 && (
            <div className="bg-white border border-zinc-200 rounded-xl overflow-hidden">
              <div className="px-4 py-2.5 border-b border-zinc-100 bg-zinc-50/60 text-sm font-bold font-display">Uploaded DCs</div>
              <div className="divide-y divide-zinc-50 max-h-64 overflow-y-auto">
                {p.dcs.map((d) => (
                  <div key={d.id} className="px-4 py-2.5 flex items-center gap-2.5 text-sm" data-testid={`dc-row-${d.id}`}>
                    <FileText className="w-4 h-4 text-zinc-400 flex-shrink-0" strokeWidth={1.5} />
                    <div className="flex-1 min-w-0">
                      <div className="font-mono text-xs font-semibold text-zinc-800">{d.dc_number}</div>
                      <div className="text-[11px] text-zinc-400">{(d.uploaded_at || "").slice(0, 10)} · {d.item_count} item{d.item_count !== 1 ? "s" : ""} · {d.uploaded_by_name}</div>
                    </div>
                    <a href={fullUrl(d.file_url)} target="_blank" rel="noreferrer"
                       className="inline-flex items-center gap-1 text-[11px] font-semibold text-blue-600 hover:underline"
                       data-testid={`dc-open-${d.id}`}>
                      <Eye className="w-3.5 h-3.5" /> View
                    </a>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
